/**
 * Payroll changed-rate queue (C5) — a work queue, not a passive report.
 *
 * Every pay rate that changed since a week was built lands here when the week
 * is submitted, with the submitter's reason. Payroll marks each reviewed before
 * the pay run. Amounts render only for a pay-rate holder; everyone else sees the
 * reason and the shift, never the number.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { schedulingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle2, Wallet } from 'lucide-react';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';

const money = (tt) => (tt == null ? '—' : `$${(tt / 10000).toFixed(2)}`);

export default function PayRateReviewQueue() {
  const perms = useEffectivePermissions();
  const showPay = perms.can && (perms.can('scheduling_pay_rates', 'view') || perms.can('compensation', 'view'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeReviewed, setIncludeReviewed] = useState(false);

  const load = () => {
    setLoading(true);
    schedulingAPI.payRateQueue({ include_reviewed: includeReviewed })
      .then(({ data }) => setRows(data.items || []))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load the queue'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeReviewed]);

  const markReviewed = async (id) => {
    try {
      await schedulingAPI.markRateReviewed(id);
      toast.success('Marked reviewed');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="pay-rate-queue">
      <div className="flex items-center gap-3">
        <Wallet className="h-6 w-6 text-emerald-600" />
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-fg tracking-tight">
            Pay-rate review queue
          </h1>
          <p className="text-sm text-fg-muted mt-1.5">
            Rates that changed since a week was built. Review each before the pay run.
          </p>
        </div>
        <label className="ml-auto flex items-center gap-2 text-sm text-fg-muted">
          <Checkbox checked={includeReviewed}
            onCheckedChange={(v) => setIncludeReviewed(Boolean(v))}
            data-testid="queue-include-reviewed" />
          Show reviewed
        </label>
      </div>

      <div className="rounded-lg border border-line overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-sunken text-fg-muted">
            <tr>
              <th className="text-left px-3 py-2">Officer</th>
              <th className="text-left px-3 py-2">Shift date</th>
              {showPay && <th className="text-right px-3 py-2">On file → Override</th>}
              <th className="text-left px-3 py-2">Reason</th>
              <th className="text-left px-3 py-2">Submitted by</th>
              <th className="text-right px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={showPay ? 6 : 5} className="px-3 py-6 text-center text-fg-muted">Loading…</td></tr>
            ) : !rows.length ? (
              <tr><td colSpan={showPay ? 6 : 5} className="px-3 py-6 text-center text-fg-muted"
                data-testid="queue-empty">Nothing waiting for review.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-line" data-testid="queue-row">
                <td className="px-3 py-2">{r.employee_name || r.employee_id}</td>
                <td className="px-3 py-2 tabular-nums">{r.shift_date}</td>
                {showPay && (
                  <td className="px-3 py-2 text-right tabular-nums">
                    {money(r.resolved_ten_thousandths)} → {money(r.current_ten_thousandths)}
                    {r.is_override ? ' (override)' : ''}
                  </td>
                )}
                <td className="px-3 py-2">{r.reason}</td>
                <td className="px-3 py-2">{r.submitted_by_email || r.submitted_by}</td>
                <td className="px-3 py-2 text-right">
                  {r.reviewed ? (
                    <span className="text-xs text-emerald-600 inline-flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Reviewed
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => markReviewed(r.id)}
                      data-testid={`queue-review-${r.id}`}>Mark reviewed</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
